import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Container, styled } from '@mui/material';
import { lowerCase, upperCase } from 'lodash';
import { createPortal } from 'react-dom';
import { Navigate } from 'react-router-dom';

import CustomBlocks from '~/blocks/CustomBlocks/CustomBlocks';
import IntroBlock from '~/components/IntroBlock/IntroBlock';
import Loader from '~/components/Loader/Loader';
import SeoBlock from '~/components/SeoBlock/SeoBlock';
import { useTenantContext } from '~/context/TenantProvider';
import { useUserInfoContext } from '~/context/UserInfoProvider';
import useAuth from '~/hooks/useAuth';
import useCustomBlocks from '~/hooks/useCustomBlocks';
import useIsAboveViewport from '~/hooks/useIsAboveViewport';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import StickyBlock from '~/pages/PricePage/StickyBlock';
import CurrentPlanPanel from '~/pages/PricePage/components/CurrentPlanPanel';
import DurationToggleButtonGroup from '~/pages/PricePage/components/DurationToggleButtonGroup';
import { safelyFilterPricePlans } from '~/pages/PricePage/safelyFilterPricePlans';
import { CurrencyParam, PricingPlansType } from '~/pages/PricePage/types';
import usePlans from '~/pages/PricePage/usePlans';
import usePriceSearchParams from '~/pages/PricePage/usePriceSearchParams';
import {
  buildSubscriptionRoute,
  MY_MEMBERSHIP_ROUTE,
  PRICING_ROUTE,
  PricingRoutes,
} from '~/routes';
import { css } from '~/styled';
import { SMALL_MARGIN_PX, SUBMIDDLE_MARGIN_PX } from '~/theme';
import { Background, Maybe, Pricing } from '~/types';
import { Select, SelectProps } from '~/ui/components/Select/Select';
import getSymbolFromCurrency from '~/ui/util/getSymbolFromCurrency';
import { checkIsIframed } from '~/utils/checkIsIframed';
import { HEADER_IMAGE_PRICING } from '~/utils/const';
import { generateSeoTitle } from '~/utils/generateSeoTitle';
import { splitBySpace } from '~/utils/stringUtils';
import { TENANTS_CONFIG } from '~/utils/tenantsConfig';
import { PriceCard, PriceCardProps, PriceCardSkeleton } from '~/xunl/pricing/PriceCard/PriceCard';
import SelectPlanButton from '~/xunl/pricing/SelectPlanButton/SelectPlanButton';

const PRICE_PAGE_SEO_IMG = '/headers-images/bg-intro.jpg';

interface PricePageProps {
  plansType: PricingPlansType;
  data?: any;
  isBlock?: boolean;
}

const INDIVIDUAL_PATHNAME = `${PRICING_ROUTE}${PricingRoutes.Individual}`;
const BUSINESS_PATHNAME = `${PRICING_ROUTE}${PricingRoutes.Business}`;

function formatPrice(val): [string | undefined, string, string] {
  const [fullPrice, currency] = splitBySpace(val);
  const [whole, decimal] = fullPrice ? fullPrice.split('.') : [];
  const currencySymbol = getSymbolFromCurrency(currency || 'GBP');

  return [currencySymbol, whole, decimal];
}

const DEFAULT_SELECTED_PLAN_INDEX = 0;

const PricePage: FC<PricePageProps> = ({
  plansType,
  data = { background: 'Dark' },
  isBlock = false,
}) => {
  const background = data?.background;
  const { tenant } = useTenantContext();
  const { userInfo, userLoading, hasAccessToContent } = useUserInfoContext();
  const { t } = useTenantTranslation();
  const { loginWithRedirect, isInitialized, isLoading, loginWithPopup } = useAuth();
  const belowFooterNodeRef = useRef(window?.document?.querySelector('#below-footer-content'));
  const plansRef = useRef<null | HTMLDivElement>(null);

  const { currencyParam, durationParam, setCurrencyParam, setDurationParam } =
    usePriceSearchParams();

  const [selectedPlanIndex, setSelectedPlanIndex] = useState(DEFAULT_SELECTED_PLAN_INDEX);

  const {
    availableCurrencies,
    availableDurations,
    plans: allPlans,
    isLoading: loadingPlans,
  } = usePlans({
    planType: plansType,
  });

  const plans = safelyFilterPricePlans(allPlans, {
    currency: currencyParam,
    duration: durationParam,
  });

  const hasCurrenciesChoices = availableCurrencies && availableCurrencies.length > 1;

  const priceOptions = availableCurrencies.map<SelectProps['options'][number]>((currency) => ({
    label: upperCase(currency),
    value: lowerCase(currency),
  }));

  const hasDurationsChoices = availableDurations && availableDurations.length > 1;

  const isBusiness = plansType === 'business';
  const isAnnual = hasDurationsChoices ? durationParam === 'annual' : true;

  const stickyVisible = useIsAboveViewport(plansRef);

  function changePlansDuration() {
    setDurationParam(durationParam === 'annual' ? 'monthly' : 'annual');
  }

  const INTRO_DATA = useMemo(
    () => ({
      heading: isBusiness ? t('page.price.unlockingPeoplePotential') : t('page.price.heading'),
      description: t(`page.price.${TENANTS_CONFIG[tenant?.name || '']?.pricingPageSeoDescKey}`),
      preview_image_url: HEADER_IMAGE_PRICING,
    }),
    [tenant, t, isBusiness],
  );

  const handleSelectPlan = (chargebeePlanId: Pricing['chargebee_plan_id']) => {
    if (checkIsIframed()) {
      loginWithPopup({
        mode: 'signUp',
        redirectUri: buildSubscriptionRoute(chargebeePlanId),
      });
    } else {
      loginWithRedirect({
        redirectUri: buildSubscriptionRoute(chargebeePlanId),
        mode: 'signUp',
      });
    }
  };

  const seoData = useMemo(
    () => ({
      seo_title: generateSeoTitle(t('page.price.joinNow'), tenant?.title),
      seo_description: t('page.price.description'),
      share_image: PRICE_PAGE_SEO_IMG,
    }),
    [tenant, t],
  );

  const { blocks } = useCustomBlocks(isBusiness ? BUSINESS_PATHNAME : INDIVIDUAL_PATHNAME);
  const isIndividual = !isBusiness;

  useEffect(() => {
    setSelectedPlanIndex(DEFAULT_SELECTED_PLAN_INDEX);
  }, [isIndividual]);

  if (!isInitialized || isLoading || userLoading) {
    return (
      <>
        <SeoBlock data={seoData} />
        <Loader />
      </>
    );
  }

  if (hasAccessToContent || userInfo?.owned_subscription || userInfo?.company) {
    return <Navigate to={MY_MEMBERSHIP_ROUTE} />;
  }

  const selectedPlan = plans[selectedPlanIndex];
  const formattedPrice = formatPrice(
    isIndividual && selectedPlan?.chargebee_plan_info?.is_taxable
      ? selectedPlan?.chargebee_plan_info?.price_with_tax
      : selectedPlan?.chargebee_plan_info?.price,
  );

  const priceCardVariant: PriceCardProps['variant'] =
    plansType === 'individual' ? 'wide' : 'default';

  function renderPlanCardsSkeleton() {
    if (priceCardVariant === 'wide') {
      return <PriceCardSkeleton variant="wide" />;
    }

    return (
      <>
        <PriceCardSkeleton variant="default" />
        <PriceCardSkeleton variant="default" />
      </>
    );
  }

  return (
    <>
      <SeoBlock data={seoData} />
      <MainWrapper
        background={background}
        isBlock={isBlock}
        className={isBlock ? 'price-block' : undefined}
      >
        <StyledContainer maxWidth={false}>
          {!isBlock && (
            <IntroBlock
              blockCss={blockCss}
              headerCss={headerCss}
              descriptionCss={descriptionCss}
              wrapperCss={wrapperCss}
              data={INTRO_DATA}
              video={false}
              hasGradient={true}
              isSmall={false}
              tenant={tenant}
            />
          )}

          <StyledPlanCardsContainer ref={plansRef} isBlock={isBlock}>
            <StyledPlanControls>
              {hasCurrenciesChoices && (
                <StyledSelect
                  label="Currency"
                  value={currencyParam}
                  options={priceOptions}
                  onChange={(value) => setCurrencyParam(value as CurrencyParam)}
                />
              )}
              {hasDurationsChoices && (
                <DurationToggleButtonGroup
                  duration={durationParam}
                  onDurationChange={setDurationParam}
                />
              )}
            </StyledPlanControls>
            <StyledPlanCardsRow>
              {loadingPlans
                ? renderPlanCardsSkeleton()
                : plans.map((plan) => (
                    <PriceCard
                      key={plan.id}
                      isAnnual={isAnnual}
                      isIndividual={isIndividual}
                      plan={plan}
                      variant={priceCardVariant}
                      onSelectPlan={handleSelectPlan}
                      hasDurationsChoices={hasDurationsChoices}
                    />
                  ))}
            </StyledPlanCardsRow>
            {!loadingPlans && <CurrentPlanPanel selectedPricingPlans={plansType} />}
          </StyledPlanCardsContainer>
        </StyledContainer>
      </MainWrapper>
      {blocks?.length > 0 && <CustomBlocks blocks={blocks} />}
      {belowFooterNodeRef.current &&
        createPortal(
          selectedPlan && (
            <StickyBlock
              visible={stickyVisible}
              formattedPrice={formattedPrice}
              isAnnual={isAnnual}
              hasDurationChoices={hasDurationsChoices}
              toggleSwitch={changePlansDuration}
              button={
                <SelectPlanButton
                  variant="sticky"
                  priceData={selectedPlan}
                  onClick={handleSelectPlan}
                />
              }
              subtitle={
                isIndividual
                  ? t('page.price.learnWithExperts')
                  : selectedPlan.enterprise
                  ? t('page.price.expandLearningOpportunities')
                  : t('page.price.manageYourTeam')
              }
              title={isIndividual ? t('page.price.becomeALearner') : selectedPlan.title}
              vatTag={selectedPlan.description}
            />
          ),
          belowFooterNodeRef.current,
        )}
    </>
  );
};

export default PricePage;

const descriptionCss = ({ theme }) => css`
  ${theme.breakpoints.up('md')} {
    color: ${theme.palette.common.gray};
  }
`;
const blockCss = ({ theme }) => css`
  ${theme.breakpoints.up('md')} {
    max-width: 680px;
  }
`;
const headerCss = ({ theme }) => css`
  ${theme.breakpoints.up('md')} {
    margin-bottom: ${SMALL_MARGIN_PX};
  }
`;
const wrapperCss = ({ theme }) => css`
  height: 580px;

  & .intro-block-container {
    padding-top: 240px;

    ${theme.breakpoints.up('md')} {
      padding-top: 250px;
    }
  }

  ${theme.breakpoints.up('md')} {
    height: 680px !important;
  }
`;
const MainWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isBlock' && prop !== 'background',
})<{ background?: Maybe<Background>; isBlock: boolean }>`
  padding-top: ${({ isBlock }) => (isBlock ? '36px' : '0px')};
  padding-bottom: ${({ isBlock }) => (isBlock ? '36px' : '0px')};
  background: ${({ theme }) => theme.palette.secondary.dark};

  &:first-child {
    margin-top: ${({ isBlock }) => (isBlock ? '76px' : '0px')};
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    padding-top: ${({ isBlock }) => (isBlock ? '72px' : isBlock)};
    padding-bottom: ${({ isBlock }) => (isBlock ? '72px' : '120px')};
    background: ${({ background, theme }) =>
      background === 'Dark' ? theme.palette.secondary.dark : theme.palette.secondary.light};

    &:first-child {
      margin-top: ${({ isBlock }) => (isBlock ? '120px' : '0px')};
    }
  }
`;
const StyledContainer = styled(Container)`
  flex: 1;
  flex-grow: 1;

  && {
    padding: 0;
  }
`;
const StyledPlanCardsContainer = styled(Container, {
  shouldForwardProp: (prop) => prop !== 'isBlock',
})<{ isBlock: boolean }>`
  position: relative;
  margin-top: ${({ isBlock }) => (isBlock ? '0px' : '-80px')};
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${SMALL_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    gap: ${SUBMIDDLE_MARGIN_PX};
  }
`;
const StyledPlanControls = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-end;
  gap: ${SMALL_MARGIN_PX};
  min-height: 120px;

  ${({ theme }) => theme.breakpoints.up('md')} {
    flex-direction: row;
    align-items: center;
    min-height: 48px;
    gap: ${SMALL_MARGIN_PX};
  }
`;
const StyledSelect = styled(Select)`
  min-width: 170px;
`;
const StyledPlanCardsRow = styled('div')`
  display: flex;
  flex-direction: column;
  gap: ${SMALL_MARGIN_PX};
  flex: 1;

  ${({ theme }) => theme.breakpoints.up('md')} {
    flex-direction: row;
    gap: ${SUBMIDDLE_MARGIN_PX};
  }
`;
